import { useEffect, useState } from 'react';
import { api, parseErr } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { fmtDate, fmtDateTime } from '@/lib/format';
import { useAuth } from '@/lib/auth';
import {
  ExternalLink, Plus, ClipboardCheck, Hammer, Camera, CheckCircle2, XCircle, Clock, AlertTriangle, Link as LinkIcon,
} from 'lucide-react';

const SD_STATUS_LABEL = {
  pending_review: 'Pending review', in_review: 'In review', approved: 'Approved',
  approved_with_comments: 'Approved w/ comments', rejected: 'Rejected', superseded: 'Superseded',
};
const SD_STATUS_CHIP = {
  pending_review: '', in_review: 'chip-info', approved: 'chip-success',
  approved_with_comments: 'chip-warning', rejected: 'chip-danger', superseded: '',
};
const MK_STATUS_LABEL = {
  planned: 'Planned', in_progress: 'In progress', ready_for_review: 'Ready for review',
  approved: 'Approved', rejected: 'Rejected', reworked: 'Reworked',
};
const MK_STATUS_CHIP = {
  planned: '', in_progress: 'chip-info', ready_for_review: 'chip-warning',
  approved: 'chip-success', rejected: 'chip-danger', reworked: 'chip-warning',
};
const CL_STATUS_LABEL = {
  pending: 'Pending', in_progress: 'In progress', complete: 'Complete', not_applicable: 'N/A',
};
const CL_STATUS_CHIP = {
  pending: '', in_progress: 'chip-info', complete: 'chip-success', not_applicable: '',
};
const CL_CATEGORY_LABEL = {
  documentation: 'Documentation', financial: 'Financial', handover: 'Handover',
  archive: 'Archive', debrief: 'Debrief',
};

const WRITE_ROLES = ['owner', 'operations', 'dm_technical', 'dm_architecture'];
const MOCKUP_REVIEW_ROLES = ['owner', 'operations', 'dm_technical', 'dm_creative', 'dm_architecture'];

export const ConstructionPanel = ({ projectId }) => {
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user.role);
  const canReviewMockup = MOCKUP_REVIEW_ROLES.includes(user.role);

  const [summary, setSummary] = useState(null);
  const [sds, setSds] = useState([]);
  const [mks, setMks] = useState([]);
  const [closure, setClosure] = useState([]);
  const [tab, setTab] = useState('shop_drawings');
  const [newSD, setNewSD] = useState(null);
  const [reviewSD, setReviewSD] = useState(null);
  const [newMK, setNewMK] = useState(null);
  const [reviewMK, setReviewMK] = useState(null);
  const [newCL, setNewCL] = useState(null);

  const load = async () => {
    try {
      const [s, sd, mk, cl] = await Promise.all([
        api.get(`/projects/${projectId}/construction_summary`),
        api.get(`/projects/${projectId}/shop_drawings`),
        api.get(`/projects/${projectId}/mockups`),
        api.get(`/projects/${projectId}/closure_items`),
      ]);
      setSummary(s.data); setSds(sd.data || []); setMks(mk.data || []); setClosure(cl.data || []);
    } catch (e) {
      // silent for team members without access
    }
  };
  useEffect(() => { load(); }, [projectId]);

  return (
    <div data-testid="construction-panel" className="mt-3">
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4" data-testid="construction-summary">
          <SumCard icon={Hammer} label="Shop drawings"
                   value={`${summary.shop_drawings.approved}/${summary.shop_drawings.total} approved`}
                   hint={summary.shop_drawings.pending ? `${summary.shop_drawings.pending} in review` : 'All reviewed'} />
          <SumCard icon={Camera} label="Mockups"
                   value={`${summary.mockups.approved}/${summary.mockups.total} approved`}
                   hint={summary.mockups.pending ? `${summary.mockups.pending} in progress` : 'All done'} />
          <SumCard icon={ClipboardCheck} label="Closure checklist"
                   value={`${summary.closure.pct}% complete`}
                   hint={`${summary.closure.complete}/${summary.closure.total} items`} />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="shop_drawings" data-testid="ca-tab-sd">Shop Drawings</TabsTrigger>
          <TabsTrigger value="mockups" data-testid="ca-tab-mk">Mockups</TabsTrigger>
          <TabsTrigger value="closure" data-testid="ca-tab-cl">Closure</TabsTrigger>
        </TabsList>

        <TabsContent value="shop_drawings">
          <div className="flex justify-end mt-3">
            {canWrite && <Button size="sm" onClick={() => setNewSD({ ref_number: '', title: '', vendor_name: '', drive_link: '', revision: 'R0', scope_tag: '' })} data-testid="ca-sd-new"><Plus size={13} className="mr-1" />New shop drawing</Button>}
          </div>
          <div className="card overflow-x-auto mt-2">
            <table className="tbl" data-testid="ca-sd-table">
              <thead><tr><th>Ref</th><th>Title</th><th>Vendor</th><th>Rev</th><th>Status</th><th>Reviewed</th><th></th></tr></thead>
              <tbody>
                {sds.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-[hsl(var(--foreground-muted))]">No shop drawings yet.</td></tr>}
                {sds.map(sd => (
                  <tr key={sd.id} data-testid={`ca-sd-row-${sd.id}`}>
                    <td className="font-mono text-[12px]">{sd.ref_number}</td>
                    <td>
                      <div>{sd.title}</div>
                      {sd.scope_tag && <div className="text-[11.5px] text-[hsl(var(--foreground-muted))]">{sd.scope_tag}</div>}
                      <a href={sd.drive_link} target="_blank" rel="noreferrer" className="text-[11.5px] text-[hsl(var(--primary))] inline-flex items-center gap-1 mt-0.5"><LinkIcon size={10} />Drive<ExternalLink size={10} /></a>
                    </td>
                    <td className="text-[12.5px]">{sd.vendor_name || '—'}</td>
                    <td className="font-mono text-[12px]">{sd.revision}</td>
                    <td><span className={`chip ${SD_STATUS_CHIP[sd.status] || ''}`}>{SD_STATUS_LABEL[sd.status] || sd.status}</span>
                      {sd.redline_drive_link && (
                        <a href={sd.redline_drive_link} target="_blank" rel="noreferrer" className="ml-2 text-[11px] text-[hsl(var(--primary))] inline-flex items-center gap-1"><LinkIcon size={9} />Redline</a>
                      )}
                    </td>
                    <td className="text-[12px] text-[hsl(var(--foreground-muted))]">{sd.reviewed_at ? fmtDate(sd.reviewed_at) : '—'}</td>
                    <td className="text-right">
                      {canWrite && (
                        <Button size="sm" variant="outline" onClick={() => setReviewSD({ ...sd, review_notes: '', redline_drive_link: '' })} data-testid={`ca-sd-review-${sd.id}`}>Review</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="mockups">
          <div className="flex justify-end mt-3">
            {canWrite && <Button size="sm" onClick={() => setNewMK({ ref_number: '', title: '', mockup_type: 'material', location: '', scheduled_at: '', vendor_name: '', photos_drive_link: '', spec_drive_link: '' })} data-testid="ca-mk-new"><Plus size={13} className="mr-1" />New mockup</Button>}
          </div>
          <div className="card overflow-x-auto mt-2">
            <table className="tbl" data-testid="ca-mk-table">
              <thead><tr><th>Ref</th><th>Title</th><th>Type</th><th>Location</th><th>Scheduled</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {mks.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-[hsl(var(--foreground-muted))]">No mockups yet.</td></tr>}
                {mks.map(mk => (
                  <tr key={mk.id} data-testid={`ca-mk-row-${mk.id}`}>
                    <td className="font-mono text-[12px]">{mk.ref_number}</td>
                    <td>
                      <div>{mk.title}</div>
                      <div className="flex gap-2 mt-0.5">
                        {mk.spec_drive_link && <a href={mk.spec_drive_link} target="_blank" rel="noreferrer" className="text-[11.5px] text-[hsl(var(--primary))] inline-flex items-center gap-1"><LinkIcon size={10} />Spec</a>}
                        {mk.photos_drive_link && <a href={mk.photos_drive_link} target="_blank" rel="noreferrer" className="text-[11.5px] text-[hsl(var(--primary))] inline-flex items-center gap-1"><Camera size={10} />Photos</a>}
                      </div>
                    </td>
                    <td className="text-[12.5px] capitalize">{mk.mockup_type || '—'}</td>
                    <td className="text-[12.5px]">{mk.location || '—'}</td>
                    <td className="text-[12.5px]">{fmtDate(mk.scheduled_at)}</td>
                    <td><span className={`chip ${MK_STATUS_CHIP[mk.status] || ''}`}>{MK_STATUS_LABEL[mk.status] || mk.status}</span></td>
                    <td className="text-right">
                      {canReviewMockup && <Button size="sm" variant="outline" onClick={() => setReviewMK({ ...mk, review_notes: '' })} data-testid={`ca-mk-review-${mk.id}`}>Review</Button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="closure">
          <div className="flex justify-end mt-3">
            {canWrite && <Button size="sm" onClick={() => setNewCL({ category: 'documentation', item: '', drive_link: '', notes: '' })} data-testid="ca-cl-new"><Plus size={13} className="mr-1" />Add item</Button>}
          </div>
          <div className="mt-2 space-y-3">
            {['documentation', 'financial', 'handover', 'archive', 'debrief'].map(cat => {
              const rows = closure.filter(c => c.category === cat);
              if (rows.length === 0) return null;
              return (
                <div key={cat} className="card p-3" data-testid={`ca-cl-cat-${cat}`}>
                  <div className="font-heading font-semibold text-[13.5px] mb-2">{CL_CATEGORY_LABEL[cat]}</div>
                  <div className="space-y-1.5">
                    {rows.map(c => (
                      <div key={c.id} className="flex items-start gap-2 py-1.5 border-b last:border-b-0" style={{ borderColor: 'hsl(var(--border))' }} data-testid={`ca-cl-row-${c.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="text-[13px]">{c.item}</div>
                          <div className="flex gap-2 mt-0.5 flex-wrap">
                            <span className={`chip ${CL_STATUS_CHIP[c.status] || ''}`}>{CL_STATUS_LABEL[c.status]}</span>
                            {c.drive_link && <a href={c.drive_link} target="_blank" rel="noreferrer" className="text-[11.5px] text-[hsl(var(--primary))] inline-flex items-center gap-1"><LinkIcon size={10} />Drive<ExternalLink size={10} /></a>}
                            {c.completed_at && <span className="text-[11px] text-[hsl(var(--foreground-subtle))]">Done {fmtDate(c.completed_at)}</span>}
                          </div>
                        </div>
                        {canWrite && (
                          <select className="h-8 text-[12px] border rounded px-1" style={{ borderColor: 'hsl(var(--border))' }}
                                  value={c.status}
                                  onChange={async (e) => {
                                    try { await api.patch(`/projects/${projectId}/closure_items/${c.id}`, { status: e.target.value }); load(); }
                                    catch (err) { toast.error(parseErr(err)); }
                                  }} data-testid={`ca-cl-status-${c.id}`}>
                            {Object.entries(CL_STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                          </select>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {closure.length === 0 && <div className="text-[13px] text-[hsl(var(--foreground-muted))] py-6 text-center card">Closure checklist will auto-populate when an Owner/Ops/DMT opens this tab.</div>}
          </div>
        </TabsContent>
      </Tabs>

      {/* NEW SHOP DRAWING */}
      <Dialog open={!!newSD} onOpenChange={o => !o && setNewSD(null)}>
        <DialogContent data-testid="ca-sd-new-dialog">
          <DialogHeader><DialogTitle>New shop drawing</DialogTitle></DialogHeader>
          {newSD && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Ref number"><Input value={newSD.ref_number} onChange={e => setNewSD({ ...newSD, ref_number: e.target.value })} data-testid="ca-sd-new-ref" /></Field>
              <Field label="Revision"><Input value={newSD.revision} onChange={e => setNewSD({ ...newSD, revision: e.target.value })} /></Field>
              <Field label="Title" full><Input value={newSD.title} onChange={e => setNewSD({ ...newSD, title: e.target.value })} data-testid="ca-sd-new-title" /></Field>
              <Field label="Vendor"><Input value={newSD.vendor_name} onChange={e => setNewSD({ ...newSD, vendor_name: e.target.value })} /></Field>
              <Field label="Scope tag"><Input value={newSD.scope_tag} onChange={e => setNewSD({ ...newSD, scope_tag: e.target.value })} /></Field>
              <Field label="Drive link" full><Input placeholder="https://drive.google.com/…" value={newSD.drive_link} onChange={e => setNewSD({ ...newSD, drive_link: e.target.value })} data-testid="ca-sd-new-link" /></Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewSD(null)}>Cancel</Button>
            <Button onClick={async () => {
              try { await api.post(`/projects/${projectId}/shop_drawings`, newSD); toast.success('Shop drawing added'); setNewSD(null); load(); }
              catch (e) { toast.error(parseErr(e)); }
            }} disabled={!newSD?.ref_number || !newSD?.title || !newSD?.drive_link} data-testid="ca-sd-new-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REVIEW SHOP DRAWING */}
      <Dialog open={!!reviewSD} onOpenChange={o => !o && setReviewSD(null)}>
        <DialogContent data-testid="ca-sd-review-dialog">
          <DialogHeader><DialogTitle>Review {reviewSD?.ref_number}</DialogTitle></DialogHeader>
          {reviewSD && (
            <>
              <Field label="Status">
                <select className="h-9 w-full border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                        value={reviewSD.status} onChange={e => setReviewSD({ ...reviewSD, status: e.target.value })} data-testid="ca-sd-review-status">
                  {Object.entries(SD_STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="Review notes (required)"><Textarea rows={3} value={reviewSD.review_notes || ''} onChange={e => setReviewSD({ ...reviewSD, review_notes: e.target.value })} data-testid="ca-sd-review-notes" /></Field>
              <Field label="Redline Drive link (optional)"><Input placeholder="https://drive.google.com/…" value={reviewSD.redline_drive_link || ''} onChange={e => setReviewSD({ ...reviewSD, redline_drive_link: e.target.value })} /></Field>
              <div className="mt-2 max-h-32 overflow-y-auto text-[12px] space-y-0.5">
                {(reviewSD.activity || []).slice().reverse().map((a, i) => (
                  <div key={i} className="font-mono text-[hsl(var(--foreground-muted))]">{fmtDateTime(a.at)} · {a.actor} · <b>{a.action}</b> — {a.comment}</div>
                ))}
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewSD(null)}>Cancel</Button>
            <Button onClick={async () => {
              try {
                const body = { status: reviewSD.status, review_notes: reviewSD.review_notes };
                if (reviewSD.redline_drive_link) body.redline_drive_link = reviewSD.redline_drive_link;
                await api.post(`/projects/${projectId}/shop_drawings/${reviewSD.id}/review`, body);
                toast.success('Review saved'); setReviewSD(null); load();
              } catch (e) { toast.error(parseErr(e)); }
            }} disabled={!reviewSD?.review_notes?.trim()} data-testid="ca-sd-review-save">Save review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NEW MOCKUP */}
      <Dialog open={!!newMK} onOpenChange={o => !o && setNewMK(null)}>
        <DialogContent data-testid="ca-mk-new-dialog">
          <DialogHeader><DialogTitle>New mockup</DialogTitle></DialogHeader>
          {newMK && (
            <div className="grid grid-cols-2 gap-3">
              <Field label="Ref number"><Input value={newMK.ref_number} onChange={e => setNewMK({ ...newMK, ref_number: e.target.value })} data-testid="ca-mk-new-ref" /></Field>
              <Field label="Type">
                <select className="h-9 w-full border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                        value={newMK.mockup_type} onChange={e => setNewMK({ ...newMK, mockup_type: e.target.value })}>
                  {['material', 'scale', 'installation', 'lighting', 'other'].map(k => <option key={k} value={k}>{k}</option>)}
                </select>
              </Field>
              <Field label="Title" full><Input value={newMK.title} onChange={e => setNewMK({ ...newMK, title: e.target.value })} data-testid="ca-mk-new-title" /></Field>
              <Field label="Location"><Input value={newMK.location} onChange={e => setNewMK({ ...newMK, location: e.target.value })} /></Field>
              <Field label="Scheduled"><Input type="date" value={newMK.scheduled_at} onChange={e => setNewMK({ ...newMK, scheduled_at: e.target.value })} /></Field>
              <Field label="Vendor"><Input value={newMK.vendor_name} onChange={e => setNewMK({ ...newMK, vendor_name: e.target.value })} /></Field>
              <Field label="Spec Drive link"><Input placeholder="https://drive.google.com/…" value={newMK.spec_drive_link} onChange={e => setNewMK({ ...newMK, spec_drive_link: e.target.value })} /></Field>
              <Field label="Photos Drive link (optional)" full><Input placeholder="https://drive.google.com/…" value={newMK.photos_drive_link} onChange={e => setNewMK({ ...newMK, photos_drive_link: e.target.value })} /></Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewMK(null)}>Cancel</Button>
            <Button onClick={async () => {
              try {
                const body = { ...newMK };
                Object.keys(body).forEach(k => { if (body[k] === '') delete body[k]; });
                await api.post(`/projects/${projectId}/mockups`, body); toast.success('Mockup added'); setNewMK(null); load();
              } catch (e) { toast.error(parseErr(e)); }
            }} disabled={!newMK?.ref_number || !newMK?.title} data-testid="ca-mk-new-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* REVIEW MOCKUP */}
      <Dialog open={!!reviewMK} onOpenChange={o => !o && setReviewMK(null)}>
        <DialogContent data-testid="ca-mk-review-dialog">
          <DialogHeader><DialogTitle>Review {reviewMK?.ref_number}</DialogTitle></DialogHeader>
          {reviewMK && (
            <>
              <Field label="Status">
                <select className="h-9 w-full border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                        value={reviewMK.status} onChange={e => setReviewMK({ ...reviewMK, status: e.target.value })} data-testid="ca-mk-review-status">
                  {Object.entries(MK_STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="Review notes (required)"><Textarea rows={3} value={reviewMK.review_notes || ''} onChange={e => setReviewMK({ ...reviewMK, review_notes: e.target.value })} data-testid="ca-mk-review-notes" /></Field>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewMK(null)}>Cancel</Button>
            <Button onClick={async () => {
              try {
                await api.post(`/projects/${projectId}/mockups/${reviewMK.id}/review`, { status: reviewMK.status, review_notes: reviewMK.review_notes });
                toast.success('Mockup reviewed'); setReviewMK(null); load();
              } catch (e) { toast.error(parseErr(e)); }
            }} disabled={!reviewMK?.review_notes?.trim()} data-testid="ca-mk-review-save">Save review</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* NEW CLOSURE ITEM */}
      <Dialog open={!!newCL} onOpenChange={o => !o && setNewCL(null)}>
        <DialogContent data-testid="ca-cl-new-dialog">
          <DialogHeader><DialogTitle>Add closure item</DialogTitle></DialogHeader>
          {newCL && (
            <>
              <Field label="Category">
                <select className="h-9 w-full border rounded-md px-2 text-[14px]" style={{ borderColor: 'hsl(var(--border))' }}
                        value={newCL.category} onChange={e => setNewCL({ ...newCL, category: e.target.value })}>
                  {Object.entries(CL_CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
              <Field label="Item"><Input value={newCL.item} onChange={e => setNewCL({ ...newCL, item: e.target.value })} data-testid="ca-cl-new-item" /></Field>
              <Field label="Drive link (optional)"><Input placeholder="https://drive.google.com/…" value={newCL.drive_link} onChange={e => setNewCL({ ...newCL, drive_link: e.target.value })} /></Field>
              <Field label="Notes (optional)"><Textarea rows={2} value={newCL.notes} onChange={e => setNewCL({ ...newCL, notes: e.target.value })} /></Field>
            </>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewCL(null)}>Cancel</Button>
            <Button onClick={async () => {
              try {
                const body = { ...newCL };
                Object.keys(body).forEach(k => { if (body[k] === '') delete body[k]; });
                await api.post(`/projects/${projectId}/closure_items`, body); toast.success('Item added'); setNewCL(null); load();
              } catch (e) { toast.error(parseErr(e)); }
            }} disabled={!newCL?.item?.trim()} data-testid="ca-cl-new-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SumCard = ({ icon: Icon, label, value, hint }) => (
  <div className="card p-4">
    <div className="flex items-start justify-between">
      <div>
        <div className="text-[11px] uppercase tracking-wider text-[hsl(var(--foreground-subtle))] font-semibold">{label}</div>
        <div className="font-heading text-xl mt-1">{value}</div>
        {hint && <div className="text-[12px] text-[hsl(var(--foreground-muted))] mt-0.5">{hint}</div>}
      </div>
      <div className="w-9 h-9 rounded-md flex items-center justify-center flex-none" style={{ background: 'hsl(var(--surface-2))', color: 'hsl(var(--foreground-muted))' }}><Icon size={16} /></div>
    </div>
  </div>
);

const Field = ({ label, full, children }) => (
  <label className={`block ${full ? 'col-span-2' : ''}`}>
    <span className="text-[12px] text-[hsl(var(--foreground-muted))]">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
